import React, {useEffect, useMemo, useState} from 'react'
import {Box, Spinner, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import Seo from '../../components/seo'
import {MAX_CACHE_AGE_L, STALE_WHILE_REVALIDATE_L} from '../../constants'
import PageNotFound from '../page-not-found'
import {useBrand} from '../../contexts/brand-context'
import {getSiteEnvVarValue} from '../../utils/site-config'

const DYN_CA_SITE_ID = 'DynamiteGarageCA'
const SUPPORTED_LOCALES = new Set(['en-ca', 'fr-ca'])

const normalizeLocaleId = (site, locale) => {
    const localeId = (locale?.id || '').toLowerCase()
    if (localeId) return localeId

    const localeAlias = (locale?.alias || '').toLowerCase()
    if (!localeAlias) return ''

    const supportedLocales = site?.l10n?.supportedLocales || []
    const resolved = supportedLocales.find((item) => item?.alias?.toLowerCase() === localeAlias)
    return (resolved?.id || localeAlias).toLowerCase()
}

const getIframeUrlByLocale = (config, siteId, localeId) => {
    const normalizedLocale = (localeId || '').toLowerCase()
    const appConfig = config?.app
    if (!appConfig || !siteId) return ''

    if (normalizedLocale === 'fr-ca') {
        return getSiteEnvVarValue(appConfig, 'APT_BOOKER_IFRAME_URLFRENCH', siteId) || ''
    }

    if (normalizedLocale === 'en-ca') {
        return getSiteEnvVarValue(appConfig, 'APT_BOOKER_IFRAME_URL', siteId) || ''
    }

    return ''
}

const isValidHttpUrl = (value) => {
    const raw = typeof value === 'string' ? value.trim() : ''
    if (!raw) return false

    try {
        const parsed = new URL(raw)
        return parsed.protocol === 'http:' || parsed.protocol === 'https:'
    } catch (_) {
        return false
    }
}

/**
 * Styling booking page (EN + FR) for DYN CA only.
 */
const StylingBooking = () => {
    const styles = useMultiStyleConfig('StylingBooking')
    const intl = useIntl()
    const {pathname} = useLocation()
    const einstein = useEinstein()
    const {res} = useServerContext()
    const appOrigin = useAppOrigin()
    const {site, locale} = useMultiSite()
    const {brandId, isDynamite} = useBrand()
    const config = getConfig()

    const localeId = normalizeLocaleId(site, locale)
    const siteId = site?.id || ''

    const shouldWaitForContext = !siteId || !localeId || !brandId

    const isDynCaContext =
        siteId === DYN_CA_SITE_ID &&
        isDynamite &&
        brandId === 'dynamite' &&
        SUPPORTED_LOCALES.has(localeId)

    const iframeSrc = useMemo(() => {
        if (!isDynCaContext) return ''

        const candidate = getIframeUrlByLocale(config, siteId, localeId)
        return isValidHttpUrl(candidate) ? candidate.trim() : ''
    }, [config, isDynCaContext, localeId, siteId])

    const [iframeLoaded, setIframeLoaded] = useState(false)

    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE_L}, stale-while-revalidate=${STALE_WHILE_REVALIDATE_L}`
        )
    }

    useEffect(() => {
        einstein.sendViewPage(pathname)
    }, [pathname, einstein])

    useEffect(() => {
        setIframeLoaded(false)
    }, [iframeSrc])

    if (shouldWaitForContext) {
        return (
            <Box data-testid="styling-booking-page" layerStyle="page" p={0}>
                <Box
                    __css={styles.loadingStateContainer}
                    aria-busy="true"
                    aria-live="polite"
                >
                    <Spinner size="xl" />
                </Box>
            </Box>
        )
    }

    if (!isDynCaContext || !iframeSrc) {
        if (res) {
            res.status(404)
        }
        return <PageNotFound />
    }

    const pageTitle = intl.formatMessage({
        id: 'styling_booking.page.title',
        defaultMessage: 'Styling booking'
    })

    const iframeTitle = intl.formatMessage({
        id: 'styling_booking.iframe.title',
        defaultMessage: 'Styling booking form'
    })

    const canonicalUrl = `${appOrigin}${pathname}`

    return (
        <Box data-testid="styling-booking-page" layerStyle="page" p={0}>
            <Seo title={pageTitle} canonicalUrl={canonicalUrl}>
                <link rel="canonical" href={canonicalUrl} />
            </Seo>
            <Box __css={styles.contentContainer}>
                {!iframeLoaded && (
                    <Box
                        __css={styles.overlaySpinnerContainer}
                        aria-busy="true"
                        aria-live="polite"
                    >
                        <Spinner size="xl" />
                    </Box>
                )}
                <iframe
                    loading="lazy"
                    id="styling-booking-iframe"
                    title={iframeTitle}
                    src={iframeSrc}
                    onLoad={() => setIframeLoaded(true)}
                    style={styles.iframe}
                />
            </Box>
        </Box>
    )
}

StylingBooking.getTemplateName = () => 'styling-booking'

export default StylingBooking
